import React, { useState, useEffect } from "react";
import "./CustomCssEditor.css";
import { DEVICES } from "../../constants/breakpoints";

const CustomCssEditor = ({ 
  selectedElement,
  setSelectedElement,
  selectedDevice,
  elements,
  setElements
}) => {
  const [customCss, setCustomCss] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [applyAnimation, setApplyAnimation] = useState(false);
  const [lastSavedCss, setLastSavedCss] = useState("");
  
  // Load existing custom CSS when selected element changes
  useEffect(() => {
    if (selectedElement?.['custom-css']?.[selectedDevice] !== undefined) {
      setCustomCss(selectedElement['custom-css'][selectedDevice]);
      setLastSavedCss(selectedElement['custom-css'][selectedDevice]);
    } else {
      // Try cascading fallback
      let fallbackCss = '';
      if (selectedDevice === DEVICES.MOBILE) {
        fallbackCss = selectedElement?.['custom-css']?.[DEVICES.TABLET] || 
                     selectedElement?.['custom-css']?.[DEVICES.DESKTOP] || '';
      } else if (selectedDevice === DEVICES.TABLET) {
        fallbackCss = selectedElement?.['custom-css']?.[DEVICES.DESKTOP] || '';
      }
      setCustomCss(fallbackCss);
      setLastSavedCss(fallbackCss);
    }
  }, [selectedElement?.id, selectedDevice]);

  // Save CSS as the user types with debounce
  useEffect(() => {
    if (!selectedElement) return;
    
    const timer = setTimeout(() => {
      if (customCss !== lastSavedCss) {
        const updatedElement = {
          ...selectedElement,
          'custom-css': {
            ...selectedElement['custom-css'],
            [selectedDevice]: customCss
          }
        };
        
        // Update the selected element
        setSelectedElement(updatedElement);
        
        // Update the elements array
        setElements(prevElements => 
          prevElements.map(element => 
            element.id === selectedElement.id ? updatedElement : element
          )
        );
        
        setLastSavedCss(customCss);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [customCss, selectedElement?.id, selectedDevice]);

  const handleCssChange = (e) => {
    setCustomCss(e.target.value);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Sci-fi themed placeholder
  const scifiPlaceholder = `/* NEUROLINK CSS INTERFACE v2.87 */
/* ELEMENT: ${selectedElement?.type?.toUpperCase() || 'UNKNOWN'} */

/* Use & to reference the current element */
& {
  /* Base styles */
  transform: rotate(5deg) scale(1.05);
  transition: all 0.3s ease;
}

/* Add hover state */
&:hover {
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(0, 195, 255, 0.3);
}

/* Add focus state */
&:focus {
  outline: none;
  border-color: #00c3ff;
}

/* Add active state */
&:active {
  transform: scale(0.98);
}

/* Add before/after elements */
&::before {
  content: '';
  position: absolute;
  background: linear-gradient(45deg, rgba(0, 195, 255, 0.1), transparent);
}

/* Target child elements */
& > .child {
  color: #00c3ff;
}

/* Target specific states of child elements */
& .child:hover {
  color: #6affbf;
}

/* @keyframes can be used in Custom CSS */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}`;

  return (
    <div className="custom-css-editor">
      <div className="section-header" onClick={toggleExpand}>
        <h4 className="section-title">
          <span className="cybr-icon">⟨⟩</span> CUSTOM CSS
          <span className="device-indicator">{selectedDevice}</span>
        </h4>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '−' : '+'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="editor-container">
          <div className="css-textarea-container">
            <textarea
              className="css-textarea"
              value={customCss}
              onChange={handleCssChange}
              placeholder={scifiPlaceholder}
              spellCheck="false"
            />
            <div className="textarea-overlay"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCssEditor; 